import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import NavLink from '../common/NavLink';
import { getIdToken } from '../Firebase';
import { slugify } from '../../utils/slugify';

const hasGroup = (group) => {
  const value = (group || '').toString().trim();
  return value && value.toLowerCase() !== 'null' && value !== 'Brak';
};

const leagueLabel = (row) => (
  hasGroup(row.leagueGroup)
    ? `${row.competitionName} (${row.leagueGroup.trim()})`
    : row.competitionName
);

const eloLabel = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const number = Number(value);
  return number > 0 ? `+${number}` : `${number}`;
};

const valueDiff = (oldValue, newValue) => (
  oldValue === newValue ? `${oldValue}` : `${oldValue} -> ${newValue}`
);

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const authHeaders = async () => {
  const token = await getIdToken(false);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EloAnomalies = () => {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await authHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/elo-anomalies`,
        { headers }
      );
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const preparePreview = async () => {
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      const headers = await authHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/elo-anomalies/preview`,
        {},
        { headers }
      );
      setPreview(response.data);
      setMessage(
        `Przygotowano podgląd: ${response.data?.matchChangesCount || 0} meczów i ${response.data?.teamChangesCount || 0} drużyn.`
      );
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const commitPreview = async () => {
    if (!preview?.previewId) return;
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      const headers = await authHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/elo-anomalies/commit`,
        { previewId: preview.previewId },
        { headers }
      );
      setMessage(response.data?.message || 'Zapisano korekty Elo.');
      setPreview(null);
      setConfirmOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setConfirmOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const renderMatch = (row) => {
    const homeUrl = `/team/${row.homeTeamId}/${slugify(row.homeTeam)}`;
    const awayUrl = `/team/${row.awayTeamId}/${slugify(row.awayTeam)}`;
    return (
      <Stack spacing={0.35}>
        <Typography component="span" sx={{ fontWeight: 700 }}>
          <Typography component={NavLink} to={homeUrl} sx={{ color: '#fff', fontWeight: 700 }}>
            {row.homeTeam}
          </Typography>
          {' '}
          {row.homeGoals}:{row.awayGoals}
          {' '}
          <Typography component={NavLink} to={awayUrl} sx={{ color: '#fff', fontWeight: 700 }}>
            {row.awayTeam}
          </Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(row.matchDate)} | match_id: {row.matchId}
        </Typography>
      </Stack>
    );
  };

  const renderLeague = (row) => {
    const competitionUrl = `/liga/${row.competitionId}/${slugify(leagueLabel(row))}`;
    return (
      <Stack spacing={0.25}>
        <Typography component={NavLink} to={competitionUrl} sx={{ color: '#90caf9', fontWeight: 700 }}>
          {leagueLabel(row)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          competition_id: {row.competitionId}
        </Typography>
      </Stack>
    );
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>Ładowanie...</Typography>
      </Box>
    );
  }

  const matchChanges = Array.isArray(preview?.matchChanges) ? preview.matchChanges : [];
  const teamChanges = Array.isArray(preview?.teamChanges) ? preview.teamChanges : [];

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
            Błędne zmiany Elo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mecze, w których znak zmiany Elo nie pasuje do zwycięzcy meczu.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="outlined" onClick={load} disabled={loading || actionLoading}>
            Odśwież
          </Button>
          <Button variant="contained" onClick={preparePreview} disabled={actionLoading || rows.length === 0}>
            {actionLoading ? <CircularProgress size={18} /> : 'Przygotuj naprawę'}
          </Button>
        </Stack>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Chip size="small" label={`Liczba meczów: ${rows.length}`} />
        {preview && <Chip size="small" color="warning" label={`Mecze do zapisu: ${matchChanges.length}`} />}
        {preview && <Chip size="small" color="warning" label={`Drużyny do zapisu: ${teamChanges.length}`} />}
      </Stack>

      <Paper sx={{ overflow: 'auto', bgcolor: '#1e1e1e', mb: 3 }}>
        <Table size="small" sx={{ minWidth: 1180 }}>
          <TableHead>
            <TableRow>
              <TableCell>Mecz</TableCell>
              <TableCell>Liga</TableCell>
              <TableCell>Home Elo</TableCell>
              <TableCell>Away Elo</TableCell>
              <TableCell>Zmiana Elo</TableCell>
              <TableCell>Źródło</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Brak wykrytych anomalii Elo.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => (
              <TableRow hover key={row.matchId}>
                <TableCell>{renderMatch(row)}</TableCell>
                <TableCell>{renderLeague(row)}</TableCell>
                <TableCell>{row.oldHomeElo}</TableCell>
                <TableCell>{row.oldAwayElo}</TableCell>
                <TableCell>
                  <Typography color="error.main" sx={{ fontWeight: 700 }}>
                    {eloLabel(row.oldEloChange)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {row.link ? (
                    <MuiLink href={row.link} target="_blank" rel="noreferrer">
                      90minut
                    </MuiLink>
                  ) : (
                    <Typography variant="body2" color="text.secondary">brak</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {preview && (
        <Paper sx={{ overflow: 'auto', bgcolor: '#1e1e1e', p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Podgląd naprawy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {preview.scope}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="warning"
              onClick={() => setConfirmOpen(true)}
              disabled={actionLoading || (matchChanges.length === 0 && teamChanges.length === 0)}
            >
              Zatwierdź i zapisz
            </Button>
          </Stack>

          <Table size="small" sx={{ minWidth: 1040 }}>
            <TableHead>
              <TableRow>
                <TableCell>Mecz</TableCell>
                <TableCell>Liga</TableCell>
                <TableCell>home_elo</TableCell>
                <TableCell>away_elo</TableCell>
                <TableCell>elo_change</TableCell>
                <TableCell>Powód</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matchChanges.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Nie ma zmian w tabeli matches do zapisania.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : matchChanges.map((row) => (
                <TableRow hover key={`preview-${row.matchId}`}>
                  <TableCell>{renderMatch(row)}</TableCell>
                  <TableCell>{renderLeague(row)}</TableCell>
                  <TableCell>{valueDiff(row.oldHomeElo, row.newHomeElo)}</TableCell>
                  <TableCell>{valueDiff(row.oldAwayElo, row.newAwayElo)}</TableCell>
                  <TableCell>{valueDiff(eloLabel(row.oldEloChange), eloLabel(row.newEloChange))}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {row.notes || 'korekta'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ fontWeight: 800, mt: 3, mb: 1 }}>
            Zmiany w teams.elo_ranking
          </Typography>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Drużyna</TableCell>
                <TableCell>Stare Elo</TableCell>
                <TableCell>Nowe Elo</TableCell>
                <TableCell>Offset</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamChanges.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Nie ma zmian w tabeli teams do zapisania.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : teamChanges.map((row) => (
                <TableRow hover key={`team-${row.teamId}`}>
                  <TableCell>
                    <Typography component={NavLink} to={`/team/${row.teamId}/${slugify(row.teamName)}`} sx={{ color: '#fff', fontWeight: 700 }}>
                      {row.teamName}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.oldEloRanking}</TableCell>
                  <TableCell>{row.newEloRanking}</TableCell>
                  <TableCell>{eloLabel(row.eloOffset)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Zatwierdzić naprawę Elo?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Zapis obejmie tylko rekordy wypisane w podglądzie: {matchChanges.length} meczów
            w tabeli matches oraz {teamChanges.length} drużyn w teams. Backend ponownie sprawdzi
            hash podglądu przed transakcją.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={actionLoading}>Anuluj</Button>
          <Button onClick={commitPreview} color="warning" variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={18} /> : 'Zapisz korektę'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EloAnomalies;
